import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { bubbleApi } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { OwnershipBadge, PermissionBadge } from "@/components/shared/MoneyDisplay";
import { Layers, ChevronDown, ChevronUp, ArrowUpRight, ArrowDownLeft, Landmark, Users } from "lucide-react";

const EVENT_ICONS = {
  CUSTOMER_PAYMENT: { icon: ArrowDownLeft, color: "text-emerald-400", bg: "bg-emerald-500/15" },
  DEPOSIT_RECEIVED: { icon: Users, color: "text-blue-400", bg: "bg-blue-500/15" },
  SUPPLIER_PAYMENT: { icon: ArrowUpRight, color: "text-amber-400", bg: "bg-amber-500/15" },
  LOAN_DRAWDOWN: { icon: Landmark, color: "text-purple-400", bg: "bg-purple-500/15" },
};

const COMPONENT_COLORS = {
  VAT: { bg: "bg-red-500/10", border: "border-red-500/20", text: "text-red-400" },
  NET_REVENUE: { bg: "bg-emerald-500/10", border: "border-emerald-500/20", text: "text-emerald-400" },
  SUPPLIER_CUSTODY: { bg: "bg-amber-500/10", border: "border-amber-500/20", text: "text-amber-400" },
  OVERHEAD: { bg: "bg-blue-500/10", border: "border-blue-500/20", text: "text-blue-400" },
  PROFIT_CANDIDATE: { bg: "bg-cyan-500/10", border: "border-cyan-500/20", text: "text-cyan-400" },
  CUSTOMER_DEPOSIT: { bg: "bg-purple-500/10", border: "border-purple-500/20", text: "text-purple-400" },
  LOAN_PROCEEDS: { bg: "bg-indigo-500/10", border: "border-indigo-500/20", text: "text-indigo-400" },
};

export default function BubblesPage() {
  const { formatMoney } = useAuth();
  const [bubbles, setBubbles] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    bubbleApi.list().then(r => { setBubbles(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const toggleExpand = (id) => setExpanded(expanded === id ? null : id);

  const formatDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="bubbles-page">
      <div>
        <h1 className="font-barlow font-bold text-2xl text-slate-50">Value Bubble Ledger</h1>
        <p className="text-sm text-slate-500 mt-0.5">Per-transaction economic truth — every payment decomposed into ownership states</p>
      </div>

      <div className="space-y-3">
        {bubbles.map((bubble) => {
          const isExpanded = expanded === bubble.id;
          const evInfo = EVENT_ICONS[bubble.event_type] || EVENT_ICONS.CUSTOMER_PAYMENT;
          const Icon = evInfo.icon;

          return (
            <Card key={bubble.id} className={`bg-slate-900/40 border-slate-800/50 transition-all ${isExpanded ? 'border-cyan-500/30 shadow-[0_0_20px_rgba(6,182,212,0.05)]' : 'hover:border-slate-700/50'}`}>
              <button
                className="w-full text-left"
                onClick={() => toggleExpand(bubble.id)}
                data-testid={`bubble-row-${bubble.id}`}
              >
                <CardHeader className="py-3 px-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-lg ${evInfo.bg} flex items-center justify-center`}>
                        <Icon className={`w-4 h-4 ${evInfo.color}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-mono text-cyan-400">{bubble.reference}</span>
                          <Badge className="text-[9px] bg-slate-800 text-slate-400 border-slate-700 border">{bubble.event_type.replace(/_/g, ' ')}</Badge>
                        </div>
                        <span className="text-xs text-slate-500">{bubble.counterparty} &middot; {formatDate(bubble.occurred_at)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-base font-mono tabular font-semibold text-slate-100">{formatMoney(bubble.gross_amount)}</span>
                      {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
                    </div>
                  </div>
                </CardHeader>
              </button>

              {isExpanded && (
                <CardContent className="px-4 pb-4 pt-0" data-testid={`bubble-detail-${bubble.id}`}>
                  <div className="border-t border-slate-800/50 pt-3">
                    {/* Summary bar */}
                    <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-4">
                      {[
                        { label: "Gross", value: bubble.gross_amount, color: "text-slate-200" },
                        { label: "Net", value: bubble.net_amount, color: "text-slate-200" },
                        { label: "Tax", value: bubble.tax_amount, color: "text-red-400" },
                        { label: "Direct Costs", value: bubble.direct_costs, color: "text-amber-400" },
                        { label: "Overhead", value: bubble.overhead_alloc, color: "text-blue-400" },
                        { label: "True Profit", value: bubble.profit_candidate, color: "text-cyan-400" },
                      ].map(item => (
                        <div key={item.label} className="text-center">
                          <div className="text-[10px] text-slate-600 uppercase tracking-wider">{item.label}</div>
                          <div className={`text-sm font-mono tabular ${item.color}`}>{formatMoney(item.value)}</div>
                        </div>
                      ))}
                    </div>

                    {/* Components */}
                    <div className="space-y-2">
                      <h4 className="text-[10px] text-slate-500 uppercase tracking-wider">Ownership Decomposition</h4>
                      {bubble.components?.map((comp, i) => {
                        const c = COMPONENT_COLORS[comp.type] || COMPONENT_COLORS.NET_REVENUE;
                        return (
                          <div key={i} className={`flex items-center justify-between p-2.5 rounded-lg ${c.bg} border ${c.border}`}>
                            <div className="flex items-center gap-3">
                              <div className={`w-2 h-2 rounded-full ${c.text.replace('text-', 'bg-')}`} />
                              <div>
                                <span className={`text-xs font-mono ${c.text}`}>{comp.type.replace(/_/g, ' ')}</span>
                                {comp.claimant && <span className="text-[10px] text-slate-500 ml-2">Claimant: {comp.claimant}</span>}
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <OwnershipBadge state={comp.ownership} />
                              <PermissionBadge permission={comp.permission} />
                              <span className={`text-sm font-mono tabular ${c.text}`}>{formatMoney(comp.amount)}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Vault routing */}
                    <div className="mt-3 p-2.5 rounded-lg bg-slate-800/30 border border-slate-700/20">
                      <span className="text-[10px] text-slate-500 uppercase tracking-wider">Vault Routing</span>
                      <div className="flex flex-wrap gap-2 mt-1.5">
                        {bubble.components?.map((comp, i) => (
                          <span key={i} className="text-[10px] font-mono text-slate-400 bg-slate-800/50 px-2 py-1 rounded">
                            {comp.type} → {comp.vault_route?.replace(/_/g, ' ')}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}
